import type { ReactNode } from 'react';
import {
  Drawer, Tabs, Space, Button, Dropdown, Tag, Typography, Spin, Flex,
} from 'antd';
import { EditOutlined, MoreOutlined } from '@ant-design/icons';

const { Text } = Typography;

// ── Types ──

export interface DetailTab {
  key: string;
  label: string;
  icon?: ReactNode;
  children: ReactNode;
}

export interface DetailAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

export interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  status?: { label: string; color: string };
  tabs?: DetailTab[];
  children?: ReactNode;
  actions?: DetailAction[];
  onEdit?: () => void;
  width?: number;
  loading?: boolean;
  extra?: ReactNode;
}

// ── DetailDrawer Component (P10: modal-first detail view) ──

export function DetailDrawer({
  open,
  onClose,
  title,
  subtitle,
  status,
  tabs,
  children,
  actions = [],
  onEdit,
  width = 720,
  loading,
  extra,
}: DetailDrawerProps) {
  const moreMenu = actions.length > 0
    ? {
        items: actions.map((a) => ({
          key: a.key,
          label: a.label,
          icon: a.icon,
          danger: a.danger,
          onClick: a.onClick,
        })),
      }
    : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      title={
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8}>
            <Text strong style={{ fontSize: 16 }}>{title}</Text>
            {subtitle && (
              <Text type="secondary" style={{ fontSize: 13 }}>{subtitle}</Text>
            )}
            {status && <Tag color={status.color}>{status.label}</Tag>}
          </Flex>
          <Space>
            {onEdit && (
              <Button icon={<EditOutlined />} onClick={onEdit}>Edit</Button>
            )}
            {moreMenu && (
              <Dropdown menu={moreMenu}>
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            )}
            {extra}
          </Space>
        </Flex>
      }
      styles={{ body: { paddingTop: tabs ? 0 : 24 } }}
    >
      {loading ? (
        <Flex justify="center" align="center" style={{ padding: 48 }}>
          <Spin />
        </Flex>
      ) : tabs ? (
        <Tabs
          items={tabs.map((t) => ({
            key: t.key,
            label: (
              <span>
                {t.icon && <span style={{ marginRight: 6 }}>{t.icon}</span>}
                {t.label}
              </span>
            ),
            children: t.children,
          }))}
        />
      ) : (
        children
      )}
    </Drawer>
  );
}
